import {
  Avatar,
  Box,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { search_result_clicked } from '@/analytics';
import { SearchHelpers } from '@/client/legacy-shared-modules/search/helpers';
import {
  IMemberSearch,
  ViewableAttributesField,
} from '@/client/legacy-shared-modules/search/search-types';
import { FormattedUserName } from '@/components/FormattedUserName';
import { MemberIndexViewableField } from '@/components/GlobalSearch/types';
import { formatDisplayName } from '@/utilities';

export const User: React.FC<{
  hit: IMemberSearch;
  memberIndexViewableField: string;
  memberIndexSearchableField: string;
  query?: string;
  position?: number;
}> = ({
  hit,
  memberIndexViewableField,
  memberIndexSearchableField,
  query,
  position,
}) => {
  const properties = hit[memberIndexViewableField as MemberIndexViewableField];
  // Must match `Configure.attributesToRetrieve` and `_highlightResult` paths for this index.
  const nameViewableField =
    memberIndexViewableField as ViewableAttributesField;
  const authorAttributes = hit[nameViewableField];

  const displayName = React.useMemo(() => {
    if (!authorAttributes) {
      return hit.displayName || '';
    }

    return formatDisplayName({
      preferredName: authorAttributes.preferredName,
      prefix: authorAttributes.prefix,
      firstName: authorAttributes.firstName,
      lastName: authorAttributes.lastName,
      suffix: authorAttributes.suffix,
      nameTitle: authorAttributes.nameTitle,
      // jobTitle is not available in post search authorAttributes
    });
  }, [authorAttributes, hit.displayName]);

  const handleClick = () => {
    search_result_clicked({
      entity: {
        id: hit.objectID,
        type: 'member',
      },
      search: {
        query: query || '',
        searchType: 'members',
      },
      engagement: {
        position,
      },
    });
  };

  return (
    <LinkBox
      as={Stack}
      direction="row"
      spacing="2"
      alignItems="center"
      p="2"
      bg="white"
      _hover={{ bg: 'gray.100' }}
      borderRadius="xl"
      cursor="pointer"
      onClick={handleClick}
    >
      <Avatar
        size="sm"
        name={displayName}
        src={hit.profilePictureUrl || undefined}
      />

      <Box>
        <LinkOverlay as={NextLink} href={SearchHelpers.getMemberUrl(hit)}>
          <Text noOfLines={1} textStyle="bodyStrong">
            <FormattedUserName
              hit={hit}
              viewableAttributesField={nameViewableField}
              highlightAttributesField={memberIndexSearchableField}
              preferMemberFullNameMatchedWordHighlights
              fallbackDisplayName={hit.displayName}
            />
          </Text>
        </LinkOverlay>

        {/* TODO: we want to highlight these but i can't figure out how to make it work */}
        {(properties?.jobTitle || properties?.company) && (
          <Text noOfLines={1} textStyle="compact" color="gray.600">
            {properties.jobTitle && properties.jobTitle}
            {properties.jobTitle && properties.company && ' at '}
            {properties.company && properties.company}
          </Text>
        )}
      </Box>
    </LinkBox>
  );
};
